// Eskom PARCC — AI workspace (Surface B of the AI Compliance Engine).
//
// A full-bleed conversational surface over the review population: ask about the
// rubric, a transaction, or a finding, and get an answer grounded in what PARCC
// already holds. Reachable only when an admin has granted `aiEnabled` AND the
// person has switched AI Mode on in Settings (see lib/aiMode) — the route guard
// and the sidebar both derive from the same `canAccessRoute` predicate, so it is
// simply absent for everybody else.
//
// STYLING NOTE. This screen is shared by BOTH themes, so its CSS lives in
// components.css WITHOUT a `[data-ui='lumen']` prefix and is driven entirely by
// design tokens. A Lumen-prefixed rule would leave Legacy unstyled, and the
// theme-parity test does not cover feature pages — it would not be caught.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Send, Sparkles, PanelRightClose, PanelRightOpen, Plus, ChevronLeft } from 'lucide-react';
import { Banner, Button } from '../../components/ui';
import { adminApi } from '../../lib/adminApi';
import { useAiAvailability } from '../../lib/aiMode';
import { useTransactions } from '../../hooks/useTransactions';
import { Link } from 'react-router-dom';

interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Cited evidence, when the engine grounded its answer. */
  citations?: { doc: string; page: number }[];
  /** Set when this turn failed, so the thread shows the error in place. */
  failed?: boolean;
}

const SUGGESTIONS: readonly string[] = [
  'Which criteria most often fail at the evaluation stage?',
  'Summarise the open gaps for my allocated transactions.',
  'What does 32-1034: 8.8.12 require at adjudication?',
];

/** A stable id without pulling in a uuid dependency. */
let turnSeq = 0;
function nextId(): string {
  turnSeq += 1;
  return `turn-${turnSeq}`;
}

/** How tall the composer may grow before it scrolls internally. Past this a
 *  pasted page would swallow the thread it is meant to sit beneath. */
const COMPOSER_MAX_PX = 180;

export function AiWorkspacePage(): React.JSX.Element {
  const ai = useAiAvailability();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Context in scope — the same register the person may already read, so the
  // panel never implies the engine sees more than they do.
  const txQuery = useTransactions();
  const scope = useMemo(() => {
    const rows = txQuery.data ?? [];
    return { transactions: rows.length };
  }, [txQuery.data]);

  // Keep the newest turn in view as the thread grows.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, busy]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed === '' || busy) return;
      setDraft('');
      setTurns((t) => [...t, { id: nextId(), role: 'user', text: trimmed }]);
      setBusy(true);
      try {
        const reply = await adminApi.aiChat({ message: trimmed });
        setTurns((t) => [
          ...t,
          { id: nextId(), role: 'assistant', text: reply.text, citations: reply.citations },
        ]);
      } catch (err) {
        setTurns((t) => [
          ...t,
          {
            id: nextId(),
            role: 'assistant',
            failed: true,
            text:
              err instanceof Error
                ? err.message
                : 'The engine could not be reached. Please try again.',
          },
        ]);
      } finally {
        setBusy(false);
        inputRef.current?.focus();
      }
    },
    [busy],
  );

  // Belt-and-braces: the route guard already refuses, but a page that can render
  // AI must never assume it. Fails closed.
  if (!ai.available) {
    return (
      <Banner tone="warn" title="AI Mode is off.">
        <span className="small">
          Switch it on in Settings — or ask an administrator to enable the AI Compliance Engine for
          your account.
        </span>
      </Banner>
    );
  }

  return (
    <div className="aiw">
      <div className="aiw-main">
        <div className="aiw-head">
          <div className="aiw-ident">
            <span className="aiw-avatar" aria-hidden>
              <Sparkles size={15} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="aiw-ident-name">Compliance copilot</div>
              <div className="small aiw-ident-sub">
                {/* Deliberately NOT "ZDR". Only the global endpoint serves
                    Anthropic on this project and Google documents it as not
                    supporting data residency, so claiming zero-data-retention
                    in a compliance tool would be asserting something the
                    codebase's own findings say is untrue. */}
                Evidence at rest in africa-south1 · inference globally routed ·
                grounded in PARCC&rsquo;s rubric and register
              </div>
            </div>
          </div>
          <div className="aiw-head-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTurns([])}
              disabled={busy || turns.length === 0}
            >
              <Plus size={14} aria-hidden /> New chat
            </Button>
            <button
              type="button"
              className="lumen-iconbtn sq36"
              aria-label={panelOpen ? 'Hide context panel' : 'Show context panel'}
              aria-expanded={panelOpen}
              onClick={() => setPanelOpen((o) => !o)}
            >
              {panelOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
            </button>
          </div>
        </div>

        <div className="aiw-thread" ref={threadRef} role="log" aria-live="polite" aria-busy={busy}>
          <div className="aiw-measure">
            {turns.length === 0 && (
              <div className="aiw-empty">
                <h2 className="aiw-empty-title">What would you like to check?</h2>
                <p className="small">
                  Answers are grounded in the transactions you can already see. The engine proposes
                  — it never records a verdict without your confirmation.
                </p>
                <div className="aiw-chips">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" className="aiw-chip" onClick={() => void send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn) =>
              turn.role === 'user' ? (
                <div key={turn.id} className="aiw-turn aiw-turn-user">
                  <div className="aiw-bubble">{turn.text}</div>
                </div>
              ) : (
                <div key={turn.id} className="aiw-turn">
                  <div className={turn.failed ? 'aiw-msg aiw-msg-bad' : 'aiw-msg'}>
                    {turn.text}
                    {/* NOT WIRED TO THE EVIDENCE VIEWER, deliberately.
                        /api/ai/chat returns { ok, text } and does no evidence
                        retrieval at all, so `citations` is never populated —
                        this branch has never rendered. Making the chips
                        clickable would be dressing up dead code and implying a
                        capability the endpoint does not have. When chat gains
                        retrieval, reuse EvidenceViewer exactly as CesPage does;
                        it needs a transaction to scope the document to, which
                        this surface does not currently have. */}
                    {turn.citations && turn.citations.length > 0 && (
                      <div className="aiw-cites">
                        {turn.citations.map((c) => (
                          <span key={`${c.doc}-${c.page}`} className="aiw-cite">
                            {c.doc} · p.{c.page}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}

            {busy && (
              <div className="aiw-turn">
                <div className="aiw-msg aiw-thinking">
                  <span className="aiw-dot" />
                  <span className="aiw-dot" />
                  <span className="aiw-dot" />
                  <span className="sr-only">Thinking…</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="aiw-composer">
          <div className="aiw-measure aiw-composer-inner">
            <textarea
              ref={inputRef}
              className="aiw-input"
              rows={1}
              value={draft}
              placeholder="Ask about a transaction, a criterion or a finding…"
              aria-label="Message the compliance copilot"
              disabled={busy}
              onChange={(e) => {
                setDraft(e.target.value);
                // Auto-grow. Reset to auto first or the box can only ever get
                // taller — scrollHeight is measured against the current height.
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_PX)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
            />
            <Button
              variant="primary"
              onClick={() => void send(draft)}
              disabled={busy || draft.trim() === ''}
              aria-label="Send message"
            >
              <Send size={15} aria-hidden />
            </Button>
          </div>
          <div className="aiw-measure small aiw-foot">
            The engine can be wrong. Every compliance verdict needs a reviewer&rsquo;s confirmation
            before it is recorded.
          </div>
        </div>
      </div>

      {/* Collapsed, the panel becomes a rail rather than vanishing — the handoff
          asks for this so the reviewer can see the context is still there and
          get it back without hunting for the header toggle. */}
      {!panelOpen && (
        <button
          type="button"
          className="aiw-rail"
          onClick={() => setPanelOpen(true)}
          aria-label="Show context and documents"
          aria-expanded={false}
        >
          <ChevronLeft size={14} aria-hidden />
          <span className="aiw-rail-label">Context &amp; documents</span>
        </button>
      )}

      {panelOpen && (
        <aside className="aiw-panel" aria-label="Context and documents">
          <div className="aiw-panel-sec">
            <div className="aiw-panel-h">Context in scope</div>
            <div className="aiw-kv">
              <span>Transactions</span>
              <b>{txQuery.isLoading ? '…' : scope.transactions.toLocaleString('en-ZA')}</b>
            </div>
            <div className="aiw-kv">
              <span>Rule book &amp; anchors</span>
              <b>Loaded</b>
            </div>
            <p className="small" style={{ marginTop: 10 }}>
              Scoped to what you may already see — the engine never reads beyond your own access.
            </p>
          </div>
          <div className="aiw-panel-sec">
            <div className="aiw-panel-h">Where verdicts happen</div>
            <p className="small">
              Per-procedure compliance verdicts are produced on a transaction&rsquo;s Compliance
              Evaluation Sheet, where you confirm or override each one. This workspace is for
              questions.
            </p>
          </div>
          <div className="aiw-panel-sec">
            <div className="aiw-panel-h">Connected actions</div>
            <nav className="aiw-actions" aria-label="Connected actions">
              <Link to="/transactions">Run a CES review</Link>
              <Link to="/findings">Findings register</Link>
              <Link to="/reports">Generate a report</Link>
            </nav>
          </div>
        </aside>
      )}
    </div>
  );
}
